// Meta Embedded Signup (with Coexistence) client flow (PRD §4.3).
//
// Loads the Facebook JS SDK, launches the "Login with Facebook" Embedded Signup
// popup (FB.login with the WA ES config id), captures the exchangeable `code`
// plus the customer's phone_number_id + waba_id (from the postMessage event),
// and hands them to the server callable which does the code→token exchange and
// creates the integration. Coexistence keeps the number on the user's phone.
//
// Config (set in admin/.env.production):
//   VITE_META_APP_ID      Facebook App id
//   VITE_META_CONFIG_ID   Facebook Login-for-Business config id (with WA ES + coexistence)
//   VITE_META_GRAPH_VERSION  optional, defaults to v21.0
import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';

declare global {
  interface Window {
    FB?: any;
    fbAsyncInit?: () => void;
  }
}

const APP_ID = import.meta.env.VITE_META_APP_ID as string | undefined;
const CONFIG_ID = import.meta.env.VITE_META_CONFIG_ID as string | undefined;
const GRAPH_VERSION = (import.meta.env.VITE_META_GRAPH_VERSION as string | undefined) ?? 'v21.0';

/** True when the Meta app is configured; the FB button hides otherwise. */
export function isEmbeddedSignupConfigured(): boolean {
  return Boolean(APP_ID && CONFIG_ID);
}

let sdkPromise: Promise<void> | null = null;

/** Inject + init the Facebook JS SDK once. */
export function loadFacebookSdk(): Promise<void> {
  if (!APP_ID) return Promise.reject(new Error('VITE_META_APP_ID not set'));
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise<void>((resolve, reject) => {
    if (window.FB) { resolve(); return; }
    window.fbAsyncInit = () => {
      window.FB.init({ appId: APP_ID, cookie: true, xfbml: false, version: GRAPH_VERSION });
      resolve();
    };
    const s = document.createElement('script');
    s.src = 'https://connect.facebook.net/en_US/sdk.js';
    s.async = true;
    s.defer = true;
    s.crossOrigin = 'anonymous';
    s.onerror = () => reject(new Error('Failed to load Facebook SDK'));
    document.body.appendChild(s);
  });
  return sdkPromise;
}

/**
 * Which onboarding Meta should run inside the popup.
 *
 *  - 'cloud_api'     — a NEW number (or one already migrated to Cloud API).
 *                      Meta's standard Embedded Signup.
 *  - 'business_app'  — a number CURRENTLY running in the WhatsApp Business app
 *                      on a phone. Meta calls this "coexistence": the popup
 *                      shows a QR the customer scans in the app, and afterwards
 *                      the number works in BOTH the app and Cloud API. This is
 *                      what lets us onboard existing businesses without them
 *                      giving up their phone (the WhatChimp-style connect).
 *
 * The two differ only in the `extras` passed to FB.login — but that difference
 * is the whole feature. `featureType: 'whatsapp_business_app_onboarding'` is
 * what makes Meta offer the "connect existing app" path at all.
 */
export type SignupMode = 'cloud_api' | 'business_app';

export interface EmbeddedSignupResult {
  code: string;
  phoneNumberId: string;
  wabaId: string;
  mode: SignupMode;
}

function extrasFor(mode: SignupMode): Record<string, unknown> {
  if (mode === 'business_app') {
    return { setup: {}, featureType: 'whatsapp_business_app_onboarding', sessionInfoVersion: '3' };
  }
  return { feature: 'whatsapp_embedded_signup', sessionInfoVersion: '3' };
}

/**
 * Launch the Embedded Signup popup and resolve with the exchangeable code +
 * the WABA / phone-number ids. Resolves only when BOTH the FB.login `code` and
 * the WA_EMBEDDED_SIGNUP postMessage (ids) have arrived.
 */
export function launchEmbeddedSignup(mode: SignupMode = 'cloud_api'): Promise<EmbeddedSignupResult> {
  return new Promise<EmbeddedSignupResult>((resolve, reject) => {
    if (!CONFIG_ID) { reject(new Error('VITE_META_CONFIG_ID not set')); return; }
    let ids: { phoneNumberId: string; wabaId: string } | null = null;
    let code: string | null = null;
    let done = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    function cleanup() {
      window.removeEventListener('message', onMessage);
      if (timer) clearTimeout(timer);
    }
    // Single settle point so a blocked/closed popup can't leave the caller
    // hanging (the FB.login callback sometimes never fires when blocked).
    function settle(err: Error | null, val?: EmbeddedSignupResult) {
      if (done) return;
      done = true;
      cleanup();
      if (err) reject(err); else resolve(val as EmbeddedSignupResult);
    }
    function maybeDone() {
      if (code && ids) settle(null, { code, phoneNumberId: ids.phoneNumberId, wabaId: ids.wabaId, mode });
    }

    function onMessage(event: MessageEvent) {
      if (!event.origin.endsWith('facebook.com')) return;
      let data: any;
      try {
        data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;
      const ev: string = data.event ?? '';
      // sessionInfoVersion 3 completion events:
      //   FINISH                                   — standard flow, number ready
      //   FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING  — coexistence flow, QR scanned
      //   FINISH_ONLY_WABA                         — user created a WABA but added
      //                                              no number: nothing to connect.
      if (ev === 'FINISH_ONLY_WABA') {
        settle(new Error('No phone number was added in the Facebook window — the WhatsApp account was created but has no number to connect.'));
        return;
      }
      if (ev === 'FINISH' || ev === 'FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING' || data.data?.phone_number_id) {
        if (!data.data?.phone_number_id || !data.data?.waba_id) {
          settle(new Error('Facebook finished without returning the phone number. Please try again.'));
          return;
        }
        ids = { phoneNumberId: String(data.data.phone_number_id), wabaId: String(data.data.waba_id) };
        maybeDone();
      } else if (ev === 'CANCEL' || ev === 'ERROR') {
        const why = data.data?.error_message ? `: ${data.data.error_message}` : '';
        settle(new Error(`Embedded Signup ${ev.toLowerCase()}${why}`));
      }
    }

    window.addEventListener('message', onMessage);

    // Safety net only. Meta's flow is multi-step (create account → profile →
    // phone → OTP → review) and routinely takes 5–10 minutes; a short timer
    // here silently discards the customer's completed signup — the window
    // finishes, but nobody is listening for the code. Popup-blocked and
    // user-closed cases are caught by the FB.login callback below, so this
    // only guards against a truly abandoned window.
    timer = setTimeout(
      () => settle(new Error('The Facebook window was left open for over 20 minutes without finishing. Please try again.')),
      20 * 60 * 1000,
    );

    loadFacebookSdk()
      .then(() => {
        window.FB.login(
          (response: any) => {
            const c = response?.authResponse?.code;
            if (c) { code = c; maybeDone(); }
            else if (!ids) {
              settle(new Error('Facebook sign-in was cancelled or the pop-up was blocked. Allow pop-ups for this site and try again.'));
            }
          },
          {
            config_id: CONFIG_ID,
            response_type: 'code',
            override_default_response_type: true,
            extras: extrasFor(mode),
          },
        );
      })
      .catch((e) => settle(e instanceof Error ? e : new Error(String(e))));
  });
}

export interface ExchangeResult {
  sandbox: boolean;
  phoneNumberId: string;
  businessAccountId: string;
  verifiedPhoneE164: string | null;
  hasToken: boolean;
  isLive: boolean;
  webhookUrl: string;
  /** The integration doc this number landed in (multi-number tenants). */
  integrationId?: string;
  mode: SignupMode;
  /** True when the number stays on the customer's phone too (Business App link). */
  coexistence: boolean;
  /** Cloud API registration of the number — required before any send works. */
  registered: boolean;
  registerError: string | null;
  /** Coexistence only: whether Meta accepted our request to sync the phone's
   *  contacts and chat history. The customer still has to approve on the phone. */
  contactSyncRequested: boolean;
  historySyncRequested: boolean;
  syncError: string | null;
  /** Set when the code→token exchange succeeded but webhook subscription failed. */
  webhookSubscribeError?: string | null;
}

const exchangeCall = httpsCallable<EmbeddedSignupResult, ExchangeResult>(
  functions,
  'whatsappExchangeEmbeddedSignup',
);

export async function exchangeEmbeddedSignup(r: EmbeddedSignupResult): Promise<ExchangeResult> {
  return (await exchangeCall(r)).data;
}
